// src/pages/Reports.jsx
import { useState, useEffect, useMemo } from "react";
import { 
  getFullReportData, getAttendanceRangeData, getAllStudents, syncAllStudentStats 
} from "../firebase/database";
import { 
  format, subDays, addDays, startOfMonth, endOfMonth, parseISO 
} from "date-fns";
import { 
  downloadBlob, getWeekRange, getMonthRange, todayStr, formatDate 
} from "../utils/helpers";
import { theme } from "../utils/theme";

const C = theme.colors;

export default function Reports() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  
  // Filters
  const [filterBatch, setFilterBatch] = useState("all");
  const [filterRole, setFilterRole] = useState("all");
  const [showInactive, setShowInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Attendance Register State
  const [registerType, setRegisterType] = useState("monthly"); // "weekly" | "monthly"
  const [activeDate, setActiveDate] = useState(todayStr());
  const [registerData, setRegisterData] = useState({}); // { date: { studentId: status } }
  const [regLoading, setRegLoading] = useState(false);

  useEffect(() => {
    loadBaseData();
  }, []);

  const loadBaseData = async () => {
    setLoading(true);
    const all = await getAllStudents();
    const activeWithStats = await getFullReportData();
    
    const merged = all.map(s => {
      const withStats = activeWithStats.find(a => a.id === s.id);
      if (withStats) return withStats;
      
      const stats = s.stats || {};
      const totalDays = stats.totalDays || 0;
      const totalPresent = stats.totalPresent || 0;
      const rate = totalDays > 0 ? Math.round((totalPresent / totalDays) * 100) : 0;
      return { ...s, rate, totalPresent, totalDays, diaryCount: stats.diaryCount || 0 };
    });

    setStudents(merged);
    setLoading(false);
  };

  // Range logic for register
  const currentRange = useMemo(() => {
    let range;
    if (registerType === "weekly") range = getWeekRange(activeDate);
    else range = getMonthRange(activeDate);
    
    const today = todayStr();
    return range.filter(date => date <= today);
  }, [registerType, activeDate]);

  useEffect(() => {
    fetchRegisterData();
  }, [currentRange]);

  const fetchRegisterData = async () => {
    if (currentRange.length === 0) return;
    setRegLoading(true);
    const data = await getAttendanceRangeData(currentRange[0], currentRange[currentRange.length - 1]);
    setRegisterData(data);
    setRegLoading(false);
  };

  const filteredStudents = useMemo(() => {
    return students.filter(s => {
      const matchBatch = filterBatch === "all" || s.batch === filterBatch;
      const matchRole = filterRole === "all" || s.role === filterRole;
      const matchStatus = showInactive || s.status !== "inactive";
      const matchSearch = !searchTerm || s.name.toLowerCase().includes(searchTerm.toLowerCase()) || (s.roll && s.roll.includes(searchTerm));
      return matchBatch && matchRole && matchStatus && matchSearch;
    });
  }, [students, filterBatch, filterRole, showInactive, searchTerm]);

  const sortedStudents = useMemo(() => {
    return [...filteredStudents].sort((a, b) => {
      const roleOrder = { "staff": 0, "trainer": 1, "instructor": 2, "student": 3 };
      const rA = roleOrder[a.role] ?? 3;
      const rB = roleOrder[b.role] ?? 3;
      if (rA !== rB) return rA - rB;
      return a.name.localeCompare(b.name);
    });
  }, [filteredStudents]);

  const batches = useMemo(() => ["all", ...new Set(students.map(s => s.batch).filter(Boolean))], [students]);

  const exportCSV = () => {
    const header = ["Roll", "Name", "Batch", "Role", "Status", "Total Days", "Present", "Rate %", "Last Seen", "Diary"];
    const rows = filteredStudents.map(s => [
      s.roll || "", 
      `"${s.name}"`, 
      s.batch || "", 
      s.role || "student", 
      s.status || "active",
      s.totalDays, 
      s.totalPresent, 
      s.rate, 
      s.lastSeen || "—", 
      s.diaryCount
    ]);
    const csv = [header, ...rows].map(r => r.join(",")).join("\n");
    downloadBlob(new Blob([csv], { type: "text/csv" }), `attendance_register_${format(new Date(), "yyyy-MM-dd")}.csv`);
  };

  const handleSync = async () => {
    if (!window.confirm("This will recalculate all attendance percentages from raw records. Continue?")) return;
    setSyncing(true);
    try {
      await syncAllStudentStats();
      await loadBaseData();
      alert("Attendance stats synchronized successfully!");
    } catch (err) {
      alert("Error syncing stats: " + err.message);
    }
    setSyncing(false);
  };

  const shiftRange = (dir) => {
    const d = parseISO(activeDate);
    const next = registerType === "weekly" 
      ? (dir === "next" ? addDays(d, 7) : subDays(d, 7))
      : (dir === "next" ? addDays(endOfMonth(d), 1) : subDays(startOfMonth(d), 1));
    setActiveDate(format(next, "yyyy-MM-dd"));
  };

  if (loading) return <div style={{ padding: 60, textAlign: "center", color: C.muted, fontFamily: "DM Sans" }}>Loading attendance register…</div>;

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        .register-container { background: #fff; border: 1px solid ${C.border}; border-radius: 16px; overflow: auto; max-height: 75vh; }
        .reg-table { border-collapse: separate; border-spacing: 0; width: 100%; }
        .reg-table th, .reg-table td { border-right: 1px solid ${C.border}; border-bottom: 1px solid ${C.border}; text-align: center; }
        .sticky-col { position: sticky; left: 0; background: #fff; z-index: 10; min-width: 200px; text-align: left !important; padding: 10px 16px !important; border-right: 2px solid ${C.border} !important; }
        .sticky-head { position: sticky; top: 0; background: #F9FAFB; z-index: 20; }
        .sticky-both { position: sticky; top: 0; left: 0; z-index: 30; background: #F9FAFB !important; border-right: 2px solid ${C.border} !important; }
        
        .reg-cell { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; margin: 0 auto; }
        .reg-P { color: ${C.green}; background: ${C.green}15; border-radius: 4px; }
        .reg-A { color: ${C.red}; background: ${C.red}15; border-radius: 4px; }
        .reg-S { color: ${C.muted}; background: #F3F4F6; border-radius: 4px; opacity: 0.5; }
        .reg-empty { color: #E5E7EB; }
        
        @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}} 
        .fade{animation:fadeIn .3s ease}
      `}</style>

      {/* Header & Main Filters */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: 0 }}>Attendance Reg.</h1>
          <p style={{ fontSize: 14, color: C.muted, marginTop: 4 }}>Daily check-in/out records and monthly summaries</p>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <button 
            onClick={handleSync} 
            disabled={syncing}
            style={{ padding: "10px 20px", borderRadius: 10, background: C.accentSoft, color: C.accent, border: `1px solid ${C.accent}33`, fontWeight: 700, fontSize: 13, cursor: syncing ? "not-allowed" : "pointer" }}
          >
            {syncing ? "Syncing..." : "🔄 Sync Stats"}
          </button>
          <button onClick={exportCSV} style={{ padding: "10px 20px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer", boxShadow: theme.shadows.accent }}>
            Export CSV ↓
          </button>
        </div>
      </div>

      {/* Common Controls (Search/Filters/Register Nav) */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ display: "flex", background: "#F3F4F6", borderRadius: 8, padding: 3, flexShrink: 0 }}>
            <button 
              onClick={() => setRegisterType("weekly")}
              style={{ padding: "6px 12px", borderRadius: 6, border: "none", background: registerType === 'weekly' ? C.accent : "none", color: registerType === 'weekly' ? "#fff" : C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer", transition: ".2s" }}
            >
              Weekly
            </button>
            <button 
              onClick={() => setRegisterType("monthly")}
              style={{ padding: "6px 12px", borderRadius: 6, border: "none", background: registerType === 'monthly' ? C.accent : "none", color: registerType === 'monthly' ? "#fff" : C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer", transition: ".2s" }}
            >
              Monthly
            </button>
          </div>
          
          <div style={{ display: "flex", alignItems: "center", gap: 4, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 8, padding: "2px 4px", flexShrink: 0 }}>
            <button onClick={() => shiftRange("prev")} style={{ padding: "4px 8px", borderRadius: 6, border: "none", background: "none", cursor: "pointer", color: C.accent, fontWeight: 800 }}>←</button>
            <div style={{ fontSize: 13, fontWeight: 700, padding: "0 8px", minWidth: 100, textAlign: "center", borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}` }}>
              {registerType === "weekly" && currentRange.length > 0
                ? `${format(parseISO(currentRange[0]), "dd MMM")} - ${format(parseISO(currentRange[currentRange.length-1]), "dd MMM")}`
                : format(parseISO(activeDate), "MMM yyyy")}
            </div>
            <button onClick={() => shiftRange("next")} style={{ padding: "4px 8px", borderRadius: 6, border: "none", background: "none", cursor: "pointer", color: C.accent, fontWeight: 800 }}>→</button>
          </div>

          <select value={filterBatch} onChange={e => setFilterBatch(e.target.value)} style={{ padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: "#fff", fontSize: 13, outline: "none", flexShrink: 0 }}>
            {batches.map(b => <option key={b} value={b}>{b === "all" ? "All Batches" : b}</option>)}
          </select>

          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, fontWeight: 600, color: C.muted, marginLeft: 8 }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer" }} />
            Show Inactive
          </label>
        </div>

        <div style={{ position: "relative", width: 150, flexShrink: 0 }}>
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: C.muted, fontSize: 12 }}>🔍</span>
          <input 
            type="text" 
            placeholder="Search..." 
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ width: "100%", padding: "8px 10px 8px 30px", borderRadius: 8, border: `1px solid ${C.border}`, outline: "none", fontSize: 13 }} 
          />
        </div>
      </div>

      {/* Register Content */}
      <div className="fade">
        {regLoading ? (
          <div style={{ padding: 60, textAlign: "center", color: C.muted }}>Loading register data...</div>
        ) : (
          <div className="register-container">
            <table className="reg-table">
              <thead>
                <tr className="sticky-head">
                  <th className="sticky-both">Roll - Student Name</th>
                  {currentRange.map(date => {
                    const d = parseISO(date);
                    const isSunday = d.getDay() === 0;
                    return (
                      <th key={date} style={{ minWidth: 40, padding: "8px 0", color: isSunday ? C.red : C.text }}>
                        <div style={{ fontSize: 10, fontWeight: 700 }}>{format(d, "EEE")}</div>
                        <div style={{ fontSize: 13 }}>{format(d, "dd")}</div>
                      </th>
                    );
                  })}
                  <th style={{ minWidth: 50, background: "#F3F4F6", fontWeight: 800, fontSize: 12 }}>TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {sortedStudents.map(s => {
                  let studentRowTotal = 0;
                  return (
                    <tr key={s.id}>
                      <td className="sticky-col">
                        <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}>
                          {s.name} {s.roll ? `(${s.roll})` : ""}
                        </div>
                      </td>
                      {currentRange.map(date => {
                        const status = registerData[date]?.[s.id];
                        const isSunday = parseISO(date).getDay() === 0;
                        const enrolledAtStr = s.enrolledAt ? s.enrolledAt.split('T')[0] : '1970-01-01';
                        const notYetEnrolled = date < enrolledAtStr;
                        const isFuture = date > todayStr();
                        
                        let cellClass = "";
                        let cellText = "";
                        
                        if (isSunday) { cellClass = "reg-S"; cellText = "S"; }
                        else if (notYetEnrolled) { cellClass = "reg-empty"; cellText = "-"; }
                        else if (status === "present") { 
                          cellClass = "reg-P"; cellText = "P"; 
                          studentRowTotal++;
                        }
                        else if (isFuture) { cellClass = "reg-empty"; cellText = "·"; }
                        else { cellClass = "reg-A"; cellText = "A"; } 

                        return (
                          <td key={date} title={`${s.name} - ${date}`} style={{ background: isSunday ? "#FEF2F2" : "inherit" }}>
                            <div className={`reg-cell ${cellClass}`} style={(cellText === "P" || cellText === "A") ? { fontSize: 16 } : {}}>{cellText}</div>
                          </td>
                        );
                      })}
                      <td style={{ background: "#F9FAFB", fontWeight: 800, color: C.accent, fontSize: 15 }}>{studentRowTotal}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: "#F3F4F6", fontWeight: 800 }}>
                  <td className="sticky-col" style={{ background: "#F3F4F6", borderRight: `2px solid ${C.border}` }}>DAILY TOTAL</td>
                  {currentRange.map(date => {
                    const dayData = registerData[date] || {};
                    const dailyPresent = sortedStudents.filter(s => dayData[s.id] === "present").length;
                    const isSunday = parseISO(date).getDay() === 0;
                    const isFuture = date > todayStr();
                    return (
                      <td key={date} style={{ color: isSunday ? C.red : C.accent, fontSize: 14 }}>
                        {(isSunday || isFuture) ? "—" : dailyPresent}
                      </td>
                    );
                  })}
                  <td style={{ background: C.accent, color: "#fff", fontSize: 16 }}>
                    {currentRange.reduce((acc, date) => {
                      if (parseISO(date).getDay() === 0 || date > todayStr()) return acc;
                      const dayData = registerData[date] || {};
                      return acc + sortedStudents.filter(s => dayData[s.id] === "present").length;
                    }, 0)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
